import { Monitor } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { useMissionScreenRequest } from '../../hooks/useMissionScreenRequest';
import { useWorkshopControl, type WorkshopControlHandle } from '../../hooks/useWorkshopControl';
import { controlView, heldByAnotherNotice, type ControlTone } from '../../lib/mission/workshopControl';
import { BTN_PRIMARY } from '../bureau/buttons';
import { relativeWhen } from './missionCardStyles';
import { WorkshopScreenPanel } from './MissionWorkshopScreen';

const STRIP: Record<ControlTone, { box: string; pill: string; dot: string }> = {
  otto: {
    box: 'bg-desk-subtle ring-desk-ring',
    pill: 'bg-desk-subtle text-desk-ink',
    dot: 'bg-desk-ink',
  },
  human: {
    box: 'bg-desk-subtle ring-desk-ring',
    pill: 'bg-desk-subtle text-desk-ink',
    dot: 'bg-desk-ink',
  },
  pending: {
    box: 'bg-desk-subtle ring-desk-line',
    pill: 'bg-desk-subtle text-desk-ink',
    dot: 'animate-pulse bg-desk-signal',
  },
  idle: {
    box: 'bg-desk-card ring-desk-line',
    pill: 'bg-desk-subtle text-desk-strong',
    dot: 'bg-desk-faint',
  },
};

/**
 * Who drives the forms browser (PLAN-ATELIER-OTTO § 16, after Muse), as the strip of the mock-up
 * « Formulaire »: « Otto pilote », « Prise de main… », « Tu as la main », « En pause », what that
 * means, « Voir l'écran », and the one clean switch. « Tu as la main » appears only once the
 * machine confirmed that no command of the workshop can reach the browser any more. Without a
 * workshop, the screen alone (it stays reachable when none runs: the header's window, the cockpit).
 */
export function WorkshopBrowser({ threadId, control, autoOpen = false }: {
  threadId: string | null;
  control: WorkshopControlHandle;
  autoOpen?: boolean;
}) {
  const { read, error, busy, take, release } = control;
  if (!read) return null;
  // Another person's mission holds the browser (lot L7e): said, and nothing to open or take.
  const held = heldByAnotherNotice(read);
  if (held) return <p role="status" className="text-sm text-desk-strong">{held}</p>;
  const view = controlView(read);
  const panel = { threadId, available: Boolean(read.screen_url), controlsAllowed: read.screen_controls, autoOpen };
  if (!view) {
    return (
      <div className="space-y-2">
        <WorkshopScreenPanel {...panel} />
        {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    );
  }
  const since = relativeWhen(read.workshop?.since ?? null);
  const tone = STRIP[view.tone];
  return (
    <section aria-label="Navigateur de l'atelier" className={twMerge('space-y-2 rounded-xl px-3 py-2.5 ring-1 ring-inset', tone.box)}>
      <WorkshopScreenPanel
        {...panel}
        lead={
          <>
            <span role="status" className={twMerge('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium', tone.pill)}>
              <span className={twMerge('h-1.5 w-1.5 rounded-full', tone.dot)} aria-hidden="true" />
              {view.label}
            </span>
            <span className="min-w-[220px] flex-1 text-sm leading-normal text-desk-strong">
              <span className="font-medium text-desk-ink">Navigateur de l'atelier</span>
              {since && view.tone !== 'idle' && <span className="text-desk-muted"> · {since}</span>} — {view.detail}
            </span>
          </>
        }
        trail={
          view.action && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void (view.action === 'take' ? take() : release())}
              className={twMerge(BTN_PRIMARY, 'h-8 px-3')}
            >
              {view.action === 'take' ? 'Prendre la main' : 'Rendre la main'}
            </button>
          )
        }
      />
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    </section>
  );
}

/**
 * The mission's strip: only for a mission that had a form workshop, and not while the mission waits
 * for a gesture on the screen — its card already shows the screen (`MissionScreenRequest`), one is enough.
 */
export function MissionWorkshopControl({ threadId }: { threadId: string }) {
  const control = useWorkshopControl(threadId);
  const gesture = useMissionScreenRequest(control.read?.workshop ? threadId : null);
  if (!control.read?.workshop || gesture.loading || gesture.request) return null;
  return <WorkshopBrowser threadId={threadId} control={control} />;
}

/** AI cockpit: the forms Chrome (log in to a site once, watch the running workshop) and who drives. */
export function FormsScreenShortcut() {
  const control = useWorkshopControl(null);
  if (!control.read) return null;
  return (
    <div className="space-y-1.5">
      <p className="flex flex-wrap items-center gap-2 text-xs text-desk-soft">
        <Monitor size={13} aria-hidden="true" /> Écran des formulaires
      </p>
      <WorkshopBrowser threadId={null} control={control} />
    </div>
  );
}
